import { usePlotBounds } from 'features/timeSeries/components/chartOverlays/usePlotBounds'
import { useRangeDrag } from 'features/timeSeries/components/chartOverlays/useRangeDrag'
import { useXScaleRedraw } from 'features/timeSeries/components/chartOverlays/useXScaleRedraw'
import {
  LabelRange,
  projectLabelSpan,
} from 'features/timeSeries/components/labelTool/labelGeometry'
import {
  WindowRoleColors,
  windowRoleColors,
} from 'features/timeSeries/components/windowsTool/windowRoleColors'
import { withCustomRanges } from 'features/timeSeries/experiment/customRanges'
import { useWindowsToolView } from 'features/timeSeries/experiment/ExperimentLabContext'
import { SplitSpec } from 'features/timeSeries/experiment/types'
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import uPlot from 'uplot'

const OverlayRoot = styled.div<{ $isActive: boolean; $resizing: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 13;
  cursor: ${({ $isActive, $resizing }) =>
    !$isActive ? 'default' : $resizing ? 'ew-resize' : 'crosshair'};
  pointer-events: ${({ $isActive }) => ($isActive ? 'all' : 'none')};
  user-select: none;
`

const WindowBand = styled.div<{
  $colors: WindowRoleColors
  $isDraft: boolean
  $hatched: boolean
}>`
  position: absolute;
  background: ${({ $colors, $hatched }) =>
    $hatched
      ? `repeating-linear-gradient(135deg, ${$colors.fill}, ${$colors.fill} 6px, transparent 6px, transparent 12px)`
      : $colors.fill};
  border-left: 1px ${({ $isDraft }) => ($isDraft ? 'dashed' : 'solid')}
    ${({ $colors }) => $colors.accent};
  border-right: 1px ${({ $isDraft }) => ($isDraft ? 'dashed' : 'solid')}
    ${({ $colors }) => $colors.accent};
  pointer-events: none;
`

const RoleChip = styled.span<{ $colors: WindowRoleColors }>`
  position: absolute;
  padding: 0 4px;
  border-radius: 2px;
  background: ${({ $colors }) => $colors.accent};
  color: #0d0d0d;
  font-family: 'Manrope', sans-serif;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  pointer-events: none;
`

const ResizeHandle = styled.div`
  position: absolute;
  width: 10px;
  cursor: ew-resize;
  z-index: 24;
  pointer-events: all;
`

interface SplitWindowsOverlayProps {
  uPlotRef: React.RefObject<uPlot | null>
}

/** Drawn train/val/test/exclude windows over the chart, committed straight
    into the experiment config's `split.custom_ranges`. */
export const SplitWindowsOverlay: React.FC<SplitWindowsOverlayProps> = ({
  uPlotRef,
}) => {
  const {
    windowDrawRole,
    setWindowDrawRole,
    config,
    patchConfig,
    showSplitWindows,
    engaged,
  } = useWindowsToolView()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const plotBounds = usePlotBounds(uPlotRef, rootRef)

  useXScaleRedraw(uPlotRef)

  const isActive = windowDrawRole !== null && patchConfig !== null
  const split = config?.split
  const ranges = useMemo(
    () => (split?.unit === 'custom' ? (split.custom_ranges ?? []) : []),
    [split]
  )

  const rangeId = (index: number): string => `window-${index}`
  const rangeAt = useCallback(
    (id: string) => {
      const index = Number(id.replace('window-', ''))
      const range = ranges[index]
      if (!range) return null
      const startMs = Date.parse(range.start)
      const endMs = Date.parse(range.end)
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null
      return { startMs, endMs }
    },
    [ranges]
  )

  const writeRanges = useCallback(
    (next: NonNullable<SplitSpec['custom_ranges']>) => {
      if (!patchConfig || !config) return
      patchConfig({ split: withCustomRanges(config.split, next) })
    },
    [patchConfig, config]
  )

  const onDraftComplete = useCallback(
    (range: LabelRange) => {
      if (!windowDrawRole) return
      writeRanges([
        ...ranges,
        {
          role: windowDrawRole,
          start: new Date(range.startMs).toISOString(),
          end: new Date(range.endMs).toISOString(),
        },
      ])
    },
    [ranges, windowDrawRole, writeRanges]
  )

  const onResizeCommit = useCallback(
    (id: string, range: LabelRange) => {
      const index = Number(id.replace('window-', ''))
      writeRanges(
        ranges.map((r, i) =>
          i === index
            ? {
                ...r,
                start: new Date(range.startMs).toISOString(),
                end: new Date(range.endMs).toISOString(),
              }
            : r
        )
      )
    },
    [ranges, writeRanges]
  )

  const {
    draft,
    resizingEdge,
    getRenderedRange,
    handleOverlayMouseDown,
    handleOverlayMouseMove,
    handleOverlayMouseUp,
    handleOverlayMouseLeave,
    handleEdgeMouseDown,
  } = useRangeDrag({
    uPlotRef,
    isActive,
    getTargetRange: rangeAt,
    onDraftComplete,
    onResizeCommit,
  })

  useLayoutEffect(() => {
    if (!isActive) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setWindowDrawRole(null)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isActive, setWindowDrawRole])

  const showPersisted =
    ranges.length > 0 && ((engaged && showSplitWindows) || isActive)
  const roleVisible = (role: string) =>
    showSplitWindows || (isActive && role === windowDrawRole)
  if (!showPersisted && !isActive) return null

  const u = uPlotRef.current
  const valToPos =
    u && plotBounds
      ? (value: number, scale: string) => u.valToPos(value, scale)
      : null
  const plotTop = plotBounds?.overTop ?? 0
  const plotBottom = plotBounds
    ? `calc(100% - ${plotBounds.overBottom}px)`
    : '0'

  const bands: React.ReactNode[] = []
  if (showPersisted) {
    ranges.forEach((range, index) => {
      if (!roleVisible(range.role || 'train')) return
      const id = rangeId(index)
      const parsed = rangeAt(id)
      if (!parsed) return
      const span = projectLabelSpan(
        getRenderedRange(id, parsed),
        valToPos,
        plotBounds
      )
      if (!span.horizontallyVisible) return
      const colors = windowRoleColors(range.role)
      bands.push(
        <React.Fragment key={id}>
          <WindowBand
            data-testid='split-window-band'
            $colors={colors}
            $isDraft={false}
            $hatched={range.role === 'exclude'}
            style={{
              left: span.startPx,
              width: Math.max(0, span.endPx - span.startPx),
              top: plotTop,
              bottom: plotBottom,
            }}
          />
          <RoleChip
            $colors={colors}
            style={{ left: span.startPx + 2, top: plotTop + 2 }}>
            {range.role || 'train'}
          </RoleChip>
          {isActive && (
            <>
              <ResizeHandle
                style={{
                  left: span.startPx - 5,
                  top: plotTop,
                  bottom: plotBottom,
                }}
                onMouseDown={(event) => handleEdgeMouseDown(id, 'start', event)}
              />
              <ResizeHandle
                style={{
                  left: span.endPx - 5,
                  top: plotTop,
                  bottom: plotBottom,
                }}
                onMouseDown={(event) => handleEdgeMouseDown(id, 'end', event)}
              />
            </>
          )}
        </React.Fragment>
      )
    })
  }

  const draftSpan = draft ? projectLabelSpan(draft, valToPos, plotBounds) : null
  const draftColors = windowRoleColors(windowDrawRole ?? 'train')

  return (
    <OverlayRoot
      ref={rootRef}
      data-testid='split-windows-overlay'
      $isActive={isActive}
      $resizing={resizingEdge !== null}
      onMouseMove={isActive ? handleOverlayMouseMove : undefined}
      onMouseDown={isActive ? handleOverlayMouseDown : undefined}
      onMouseUp={isActive ? handleOverlayMouseUp : undefined}
      onMouseLeave={isActive ? handleOverlayMouseLeave : undefined}>
      {bands}
      {draftSpan?.horizontallyVisible && (
        <WindowBand
          data-testid='split-window-draft'
          $colors={draftColors}
          $isDraft
          $hatched={windowDrawRole === 'exclude'}
          style={{
            left: draftSpan.startPx,
            width: Math.max(0, draftSpan.endPx - draftSpan.startPx),
            top: plotTop,
            bottom: plotBottom,
          }}
        />
      )}
    </OverlayRoot>
  )
}
